import {
  CategoryOption, LevelOption,
  PLSession,
  SubjectSummary,
  SubjectOption, StatementSelectionsData, SelectedNeedsData, AlignedGoals, SharedSubjectSummary
} from '@/presentlevels/presentlevels_types.ts'
import { sendHTTPRequest, safeJsonParse } from "@/util/http.ts";
import { goalbookUrls, toolkitUrls } from '@/util/links.js'
import { getCookie, getHandleRequest } from '@/authentication/request_handler.ts'
import { Standard } from '@/standards/standard_types.ts'

export async function getPresentlevelsSession(sessionUUID: string): Promise<PLSession> {
  try {
    const requestFn = async () => {
      return await fetch(`${goalbookUrls.presentlevels}/api/v2/pl_sessions/${sessionUUID}`, {
        method: 'GET',
        credentials: 'include'
      })
    }
    const requestHandler = getHandleRequest()
    return await requestHandler({ requestFn }) as PLSession
  } catch (e) {
    throw new Error('Error Getting Session', { cause: e })
  }
}

export async function postPresentlevelsSession(body: {
  student_name: string,
  enrolled_grade_uuid: string,
  selected_subject_uuids: string[]
}): Promise<PLSession> {
  const requestFn = async () => {
    const gbcs = getCookie('gbcs')
    return await fetch(`${goalbookUrls.presentlevels}/api/v2/pl_sessions/`,{
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'X-CSRF-Token': gbcs
      },
      body: JSON.stringify(body),
      credentials: 'include'
    })
  }
  try {
    const requestHandler = getHandleRequest()
    return await requestHandler({ requestFn }) as PLSession
  } catch (e) {
    throw new Error('Error Creating Session', { cause: e })
  }
}

export async function getSubjectSummariesForSession(sessionUUID: string): Promise<SubjectSummary[]> {
  try {
    const options = new URLSearchParams({ pl_session_uuid: sessionUUID})
    const requestFn = async () => {
      return await fetch(`${goalbookUrls.presentlevels}/api/v2/summaries?${options.toString()}`, {
        method: 'GET',
        credentials: 'include'
      })
    }
    const requestHandler = getHandleRequest()
    return await requestHandler({ requestFn }) as SubjectSummary[]
  } catch (e) {
    throw new Error('Error Getting Subject Summaries', { cause: e })
  }
}

export async function getSubjectSummaryByUUID(summaryUUID: string): Promise<SubjectSummary> {
  try {
    const requestFn = async () => {
      return await fetch(`${goalbookUrls.presentlevels}/api/v2/summaries/${summaryUUID}`, {
        method: 'GET',
        credentials: 'include'
      })
    }
    const requestHandler = getHandleRequest()
    return await requestHandler({ requestFn }) as SubjectSummary
  } catch (e) {
    throw new Error('Error Getting Subject Summary', { cause: e })
  }
}

export async function postSubjectSummary(body: {
  student_name: string,
  subject_uuid: string,
  subject_label: string,
  grade_uuid: string,
  grade_label: string,
  pl_session_uuid: string
  statement_text?: string,
  aligned_goals?: AlignedGoals
  standards?: Standard[]
}): Promise<SubjectSummary> {
  try {
    const requestFn = async () => {
      const gbcs = getCookie('gbcs')
      return await fetch(`${goalbookUrls.presentlevels}/api/v2/summaries/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-Token': gbcs
        },
        body: JSON.stringify(body),
        credentials: 'include'
      })
    }
    const requestHandler = getHandleRequest()
    return await requestHandler({ requestFn }) as SubjectSummary
  } catch (e) {
    throw new Error('Error Creating Subject Summary', { cause: e })
  }
}

export async function postCategorySelections(args: StatementSelectionsData): Promise<SubjectSummary> {
  try {
    const requestFn = async () => {
      const gbcs = getCookie('gbcs')
      const queryParams = new URLSearchParams()
      if (args.fetchGoals !== undefined) {
        queryParams.append('fetchgoals', String(args.fetchGoals))
      }
      if (args.beta !== undefined) {
        queryParams.append('beta', String(args.beta))
      }
      if (args.content_profile !== undefined) {
        queryParams.append('content_profile', String(args.content_profile))
      }
      const queryString = queryParams.toString() ? `?${queryParams.toString()}` : ''
      return await fetch(`${goalbookUrls.presentlevels}/api/v2/summaries/${args.summaryUUID}/statement/${queryString}`,{
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-Token': gbcs
        },
        body: JSON.stringify({
          category_label: args.categoryLabel,
          category_uuid: args.categoryUUID,
          strengths: args.strengths,
          needs: args.needs,
          plw_trace: args.plw_trace,
          save_to_summary: args.saveToSummary,
          statement_text: args.statementText,
          aligned_goals: args.alignedGoals,
          effect_statement_needs_update: args.effect_statement_needs_update,
        }),
        credentials: 'include'
      })
    }
    const requestHandler = getHandleRequest()
    return await requestHandler({ requestFn }) as SubjectSummary
  } catch (e) {
    throw new Error('Error Submitting Category Selections', { cause: e })
  }
}

export async function postGenerateEffectStatement(
  summaryUUID: string,
  args: {
    statement_text: string,
    save_to_summary?: boolean,
    plw_trace?: string
  },
  beta?: boolean
): Promise<SubjectSummary> {
  try {
    const requestFn = async () => {
      const gbcs = getCookie('gbcs')
      const queryParams = new URLSearchParams()
      if (beta !== undefined) {
        queryParams.append('beta', String(beta))
      }
      const queryString = queryParams.toString() ? `?${queryParams.toString()}` : ''
      return await fetch(`${goalbookUrls.presentlevels}/api/v2/summaries/${summaryUUID}/effect${queryString}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-Token': gbcs
        },
        body: JSON.stringify({
          statement_text: args.statement_text,
          save_to_summary: args.save_to_summary,
          plw_trace: args.plw_trace
        }),
        credentials: 'include'
      })
    }
    const requestHandler = getHandleRequest()
    return await requestHandler({ requestFn }) as SubjectSummary
  } catch (e) {
    throw new Error('Error Generating Effect Statement', { cause: e })
  }
}

export async function putSubjectSummary(summaryUUID: string, fieldsToUpdate: Partial<SubjectSummary>): Promise<SubjectSummary> {
  try {
    const requestFn = async () => {
      const gbcs = getCookie('gbcs')
      return await fetch(`${goalbookUrls.presentlevels}/api/v2/summaries/${summaryUUID}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-Token': gbcs
        },
        body: JSON.stringify(fieldsToUpdate),
        credentials: 'include'
      })
    }
    const requestHandler = getHandleRequest()
    return await requestHandler({ requestFn }) as SubjectSummary
  } catch (e) {
    throw new Error('Error Updating Summary', { cause: e })
  }
}

export async function getSubjectOptions(): Promise<SubjectOption[]> {
  try {
    const requestFn = async () => {
      const gbcs = getCookie('gbcs')
      return await fetch(`${goalbookUrls.presentlevels}/api/v2/subjects/`, {
        method: 'GET',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-Token': gbcs
        },
        credentials: 'include'
      })
    }
    const requestHandler = getHandleRequest()
    return await requestHandler({ requestFn }) as SubjectOption[]
  } catch (e) {
    throw new Error('Error Getting Subject Options', { cause: e })
  }
}

export async function getLevelOptions(): Promise<LevelOption[]> {
  try {
    const response = await sendHTTPRequest({
      url: `${goalbookUrls.presentlevels}/api/v2/levels/`,
      method: 'GET',
      includeCredentials: true
    })
    return await safeJsonParse(response) as LevelOption[]
  } catch (e) {
    throw new Error('Error Getting Level Options', { cause: e })
  }
}

export async function getSubjectCategoriesWithSkills(subjectUUID: string): Promise<CategoryOption[]> {
  try {
    const response = await sendHTTPRequest({
      url: `${goalbookUrls.presentlevels}/api/v2/subjects/${subjectUUID}`,
      method: 'GET',
      includeCredentials: true
    })
    return await safeJsonParse(response) as CategoryOption[]
  } catch (e) {
    throw new Error('Error Getting Subject Categories', { cause: e })
  }
}

export async function deleteSubjectSummary(summaryUUID: string): Promise<void> {
  try {
    const requestFn = async () => {
      const gbcs = getCookie('gbcs')
      return await fetch(`${goalbookUrls.presentlevels}/api/v2/summaries/${summaryUUID}`,{
        method: 'DELETE',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-Token': gbcs
        },
        credentials: 'include'
      })
    }
    const requestHandler = getHandleRequest()
    await requestHandler({ requestFn })
  } catch (e) {
    throw new Error('Error deleting subject summary', { cause: e })
  }
}

export async function postAlignedGoalsForSummary(
  summaryUUID: string,
  args: {
    category_label: string,
    subject_label: string,
    grade_label: string,
    needs: SelectedNeedsData[],
    plw_trace?: string
  },
  beta?: boolean,
  content_profile?: string
): Promise<AlignedGoals> {
  try {
    const requestFn = async () => {
      const gbcs = getCookie('gbcs')
      const queryParams = new URLSearchParams()
      if (beta) {
        queryParams.append('beta', 'true')
      }
      if (content_profile) {
        queryParams.append('content_profile', content_profile)
      }
      const queryString = queryParams.toString() ? `?${queryParams.toString()}` : ''
      const url = `${goalbookUrls.presentlevels}/api/v2/summaries/${summaryUUID}/aligned_goals/${queryString}`
      return await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-Token': gbcs
        },
        credentials: 'include',
        body: JSON.stringify(args)
      })
    }
    const requestHandler = getHandleRequest()
    return await requestHandler({ requestFn }) as AlignedGoals
  } catch (e) {
    throw new Error('Error Getting Aligned Goals For Summary', { cause: e })
  }
}

// ── Assessment Upload (AI-powered PLAAFP generation) ──────────────────────────

export interface AssessmentItem {
  id: string
  title: string
  description: string
  dataRef: string
  selected: boolean
  custom: boolean
}

export interface AssessmentAnalysisResult {
  assessmentType: string
  studentFirstName: string | null
  studentClassification: string | null
  assessmentDate: string | null
  topLevelMetrics: {
    scaledScore?: number
    percentileRank?: number
    grade?: string
    benchmarkCategory?: string
    gradeEquivalent?: string
    estimatedOralFluency?: number
    literacyClassification?: string
    instructionalReadingLevel?: string
  }
  strengths: AssessmentItem[]
  needs: AssessmentItem[]
  educatorInsightQuestions: string[]
}

export interface AssessmentValidationError {
  type: string
  title: string
  message: string
}

export class AssessmentValidationException extends Error {
  validationError: AssessmentValidationError
  constructor(validationError: AssessmentValidationError) {
    super(validationError.message)
    this.validationError = validationError
  }
}

export async function postAnalyzeAssessment({
  fileData,
  mediaType,
  filename,
  beta,
  content_profile,
  report_type,
  subject
}: {
  fileData: string
  mediaType: string
  filename: string
  beta?: boolean
  content_profile?: string
  report_type?: string
  subject?: string
}): Promise<AssessmentAnalysisResult> {
  try {
    const requestFn = async () => {
      const gbcs = getCookie('gbcs')
      const queryParams = new URLSearchParams()
      if (beta) {
        queryParams.append('beta', 'true')
      }
      if (content_profile) {
        queryParams.append('content_profile', content_profile)
      }
      if (report_type) {
        queryParams.append('report_type', report_type)
      }
      if (subject) {
        queryParams.append('subject', subject)
      }
      const queryString = queryParams.toString() ? `?${queryParams.toString()}` : ''
      const url = `${goalbookUrls.presentlevels}/api/v2/report/upload/analyze${queryString}`
      return await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-Token': gbcs
        },
        credentials: 'include',
        body: JSON.stringify({ fileData, mediaType, filename })
      })
    }
    const requestHandler = getHandleRequest()
    const result = await requestHandler({ requestFn }) as any
    
    // Check if the response contains a validation error
    if (result.validationError) {
      throw new AssessmentValidationException(result.validationError)
    }
    
    return result as AssessmentAnalysisResult
  } catch (e: any) {
    // Re-throw AssessmentValidationException as-is
    if (e instanceof AssessmentValidationException) {
      throw e
    }
    
    // Check if the error response contains a validationError (e.g., 422 status)
    // The request handler may have parsed the response body before throwing
    if (e.validationError) {
      throw new AssessmentValidationException(e.validationError)
    }
    
    throw new Error('Error Analyzing Assessment', { cause: e })
  }
}

export async function postGenerateAssessmentNarrative(payload: {
  assessmentType: string | null
  studentFirstName: string | null
  assessmentDate: string | null
  topLevelMetrics: {
    scaledScore?: number
    percentileRank?: number
    grade?: string
    benchmarkCategory?: string
    gradeEquivalent?: string
    estimatedOralFluency?: number
    literacyClassification?: string
    instructionalReadingLevel?: string
  }
  selectedStrengths: AssessmentItem[]
  selectedNeeds: AssessmentItem[]
  educatorInsights: Array<{ question: string; response: string }>
}, beta?: boolean, content_profile?: string, report_type?: string, subject?: string): Promise<{ statement: string }> {
  try {
    const requestFn = async () => {
      const gbcs = getCookie('gbcs')
      const queryParams = new URLSearchParams()
      if (beta) {
        queryParams.append('beta', 'true')
      }
      if (content_profile) {
        queryParams.append('content_profile', content_profile)
      }
      if (report_type) {
        queryParams.append('report_type', report_type)
      }
      if (subject) {
        queryParams.append('subject', subject)
      }
      const queryString = queryParams.toString() ? `?${queryParams.toString()}` : ''
      const url = `${goalbookUrls.presentlevels}/api/v2/report/upload/generate${queryString}`
      return await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-Token': gbcs
        },
        credentials: 'include',
        body: JSON.stringify(payload)
      })
    }
    const requestHandler = getHandleRequest()
    return await requestHandler({ requestFn }) as { statement: string }
  } catch (e) {
    throw new Error('Error Generating Assessment Narrative', { cause: e })
  }
}

export async function getSharedPresentLevelsStatement(uuid: string): Promise<SharedSubjectSummary> {
  const url = `${toolkitUrls.apiBases.library}/shared/presentlevels/summaries/${uuid}`
  const response = await fetch(url, {
    method: 'GET'
  })

  if (!response.ok) {
    throw new Error(`Failed to fetch shared Present Levels statement: ${response.status}`)
  }

  return await safeJsonParse(response) as SharedSubjectSummary
}
